<template>
  <section class="ws-act-gantt-section">
    <div class="ws-act-gantt-head">
      <h2 class="ws-act-gantt-head__title">{{ t('workspaceAtividade.gantt.title') }}</h2>
      <span v-if="filterUsuarioId" class="ws-act-list-head__count">
        {{ userName(filterUsuarioId) }}
      </span>
    </div>
    <div class="ws-act-gantt">
      <div class="ws-act-gantt__scroll">
        <div class="ws-act-gantt__grid" :style="{ minWidth: `${gridMinWidth}px` }">
          <div class="ws-act-gantt__header">
            <div class="ws-act-gantt__label-col ws-act-gantt__label-col--head">
              {{ t('workspaceAtividade.gantt.task') }}
            </div>
            <div class="ws-act-gantt__timeline-head">
              <div
                v-for="tick in ticks"
                :key="tick.key"
                class="ws-act-gantt__tick"
                :style="{ left: `${tick.pct}%` }"
              >
                {{ tick.label }}
              </div>
            </div>
          </div>

          <div v-for="row in rows" :key="row.taskId" class="ws-act-gantt__row">
            <div class="ws-act-gantt__label-col">
              <span class="ws-act-gantt__task-color" :style="{ backgroundColor: row.taskCor || '#6366f1' }" />
              <div class="ws-act-gantt__task-info">
                <span class="ws-act-gantt__task-title" :title="row.taskTitulo">{{ row.taskTitulo }}</span>
                <div class="ws-act-gantt__avatars">
                  <span
                    v-for="uid in row.usuarioIds"
                    :key="uid"
                    class="ws-act-gantt__avatar-dot"
                    :style="{ backgroundColor: userColor(uid) }"
                    :title="userName(uid)"
                  />
                </div>
              </div>
            </div>
            <div class="ws-act-gantt__track">
              <div
                v-for="tick in ticks"
                :key="'line-' + tick.key"
                class="ws-act-gantt__grid-line"
                :style="{ left: `${tick.pct}%` }"
              />
              <button
                v-for="bar in row.bars"
                :key="bar.id"
                type="button"
                class="ws-act-gantt__bar"
                :class="{ 'ws-act-gantt__bar--active': !bar.dataFim }"
                :style="barStyle(bar)"
                :title="barTitle(bar)"
                @click="$emit('open-task', bar.taskId)"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="ws-act-gantt__legend">
        <span class="ws-act-gantt__legend-item">
          <span class="ws-act-gantt__legend-swatch" style="background: #5865f2" />
          {{ t('workspaceAtividade.gantt.legendSession') }}
        </span>
        <span class="ws-act-gantt__legend-item">
          <span class="ws-act-gantt__legend-swatch ws-act-gantt__legend-swatch--active" />
          {{ t('workspaceAtividade.gantt.legendActive') }}
        </span>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const props = defineProps({
  sessoes: { type: Array, default: () => [] },
  periodoInicio: { type: String, required: true },
  periodoFim: { type: String, required: true },
  filterUsuarioId: { type: [Number, String], default: null },
  resumoUsuarios: { type: Array, default: () => [] }
})

defineEmits(['open-task'])

const USER_COLORS = [
  '#5865f2', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#ef4444', '#14b8a6'
]

const rangeStart = computed(() => new Date(`${props.periodoInicio}T00:00:00`).getTime())
const rangeEnd = computed(() => new Date(`${props.periodoFim}T23:59:59`).getTime())
const rangeMs = computed(() => Math.max(1, rangeEnd.value - rangeStart.value))

const filteredSessoes = computed(() => {
  let list = props.sessoes || []
  if (props.filterUsuarioId != null) {
    list = list.filter((s) => String(s.usuarioId) === String(props.filterUsuarioId))
  }
  return list
})

const userMap = computed(() => {
  const m = {}
  for (const u of props.resumoUsuarios || []) {
    m[u.usuarioId] = u.nome
  }
  for (const s of props.sessoes || []) {
    m[s.usuarioId] = s.usuarioNome
  }
  return m
})

const userColor = (uid) => USER_COLORS[Math.abs(Number(uid) || 0) % USER_COLORS.length]
const userName = (uid) => userMap.value[uid] || t('workspaceAtividade.gantt.user')

const rows = computed(() => {
  const byTask = new Map()
  for (const s of filteredSessoes.value) {
    if (!byTask.has(s.taskId)) {
      byTask.set(s.taskId, {
        taskId: s.taskId,
        taskTitulo: s.taskTitulo,
        taskCor: s.taskCor,
        usuarioIds: new Set(),
        bars: []
      })
    }
    const row = byTask.get(s.taskId)
    row.usuarioIds.add(s.usuarioId)
    row.bars.push(s)
  }
  return [...byTask.values()].map((r) => ({
    ...r,
    usuarioIds: [...r.usuarioIds]
  }))
})

const dayCount = computed(() => {
  const a = new Date(props.periodoInicio)
  const b = new Date(props.periodoFim)
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
})

const gridMinWidth = computed(() => Math.max(900, dayCount.value * 52 + 260))

const ticks = computed(() => {
  const count = Math.min(dayCount.value, 16)
  const step = Math.max(1, Math.floor(dayCount.value / count))
  const items = []
  const start = new Date(props.periodoInicio)
  const loc = locale.value === 'en' ? 'en-US' : locale.value === 'es' ? 'es-ES' : 'pt-BR'
  for (let i = 0; i < dayCount.value; i += step) {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const ts = d.getTime()
    const pct = ((ts - rangeStart.value) / rangeMs.value) * 100
    items.push({
      key: i,
      pct: Math.min(100, Math.max(0, pct)),
      label: d.toLocaleDateString(loc, { day: '2-digit', month: 'short' })
    })
  }
  return items
})

const clipTimes = (s) => {
  const ini = new Date(s.dataInicio).getTime()
  const fimRaw = s.dataFim ? new Date(s.dataFim).getTime() : Date.now()
  const fim = Math.min(fimRaw, rangeEnd.value)
  const start = Math.max(ini, rangeStart.value)
  const end = Math.min(fim, rangeEnd.value)
  return { start, end }
}

const barStyle = (s) => {
  const { start, end } = clipTimes(s)
  const left = ((start - rangeStart.value) / rangeMs.value) * 100
  const width = Math.max(0.35, ((end - start) / rangeMs.value) * 100)
  const baseColor = userColor(s.usuarioId)
  return {
    left: `${left}%`,
    width: `${width}%`,
    backgroundColor: baseColor,
    top: `${(hashLane(s.usuarioId) % 3) * 26 + 10}px`
  }
}

const hashLane = (uid) => Math.abs(Number(uid) || 0)

const barTitle = (s) => {
  const ini = new Date(s.dataInicio).toLocaleString()
  const fim = s.dataFim ? new Date(s.dataFim).toLocaleString() : t('workspaceAtividade.gantt.inProgress')
  return `${s.usuarioNome} · ${s.taskTitulo}\n${ini} → ${fim}`
}
</script>

<style scoped>
.ws-act-list-head__count {
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 4px;
  background: color-mix(in srgb, #06b6d4 12%, var(--bg-secondary));
  color: #0891b2;
}
</style>
